import React, { useReducer, useMemo, useEffect, useCallback, useState, useRef } from 'react'
import CustomForm from './CustomForm'
import { useDomainChecker } from '../../utils/hooks/useDomainChecker'
import { useCsrf } from '../../utils/context/CsrfContext'
import { startOauthFlow } from '../../utils/oauth-init'
import { useAuth } from '../../utils/context/AuthContext'
import useGetQueryString from '../../utils/hooks/useQueryString'
import { ReactComponent as HarborLogo } from '../../images/HFLogoOnlyDark.svg'
import { ReactComponent as GoogleLogo } from '../../assets/images/icons/icons8-google.svg'
import { ReactComponent as AppleLogo } from '../../assets/images/logos/Apple_logo_black.svg'
import { ReactComponent as LinkedInLogo } from '../../assets/images/logos/LinkedIn_icon.svg'
import * as styles from '../../styles/components/cognito-register-form.module.scss'

const STAGE = { SIGNUP: 'signup', CONFIRM: 'confirm', THANKYOU: 'thankyou', FINAL_THANKS: 'final_thanks' }
const SESSION_EMAIL_KEY = 'signupEmail'
const DOMAIN_STATUS_KEY = 'domainStatus'
const isBrowser = typeof window !== 'undefined'

const readStorageValue = (key) => {
  if (!isBrowser) return ''
  try {
    const sessionValue = window.sessionStorage.getItem(key)
    if (sessionValue) return sessionValue
  } catch (_) { /* ignore storage errors */ }

  try {
    const localStore = window.localStorage
    if (!localStore) return ''
    const localValue = localStore.getItem(key)
    return localValue || ''
  } catch (_) {
    return ''
  }
}

const writeStorageValue = (key, value) => {
  if (!isBrowser) return
  const shouldStore = typeof value === 'string' && value.length > 0
  try {
    if (shouldStore) window.sessionStorage.setItem(key, value)
    else window.sessionStorage.removeItem(key)
  } catch (_) { /* ignore storage errors */ }

  try {
    const localStore = window.localStorage
    if (!localStore) return
    if (shouldStore) localStore.setItem(key, value)
    else localStore.removeItem(key)
  } catch (_) {
    /* ignore storage errors */
  }
}

const initialState = {
  stage: STAGE.SIGNUP,
  form: { email: '', given_name: '', family_name: '', request_institutional_access: false },
  confirm: { code: '', password: '' },
  userSub: '',
  domainStatus: '',
  done: false
}

function reducer(state, a) {
  switch (a.type) {
    case 'SET_STAGE': return { ...state, stage: a.stage }
    case 'FORM': return { ...state, form: a.data }
    case 'CONFIRM': return { ...state, confirm: a.data }
    case 'SET_CONFIRM_CODE': return { ...state, confirm: { ...state.confirm, code: a.code || '' } }
    case 'USER_SUB': return { ...state, userSub: a.sub }
    case 'DOMAIN': return { ...state, domainStatus: a.status }
    case 'DONE': return { ...state, done: true }
    default: return state
  }
}

function OrDivider({ children = 'OR SIGN UP WITH EMAIL' }) {
  return (
    <div className={styles.orDivider}>
      <span>{children}</span>
    </div>
  )
}

export default function CognitoRegisterForm({ onSuccess }) {
  const { getQueryParam } = useGetQueryString()
  const userStr = getQueryParam('user')
  const codeStr = getQueryParam('confirmation_code')
  const initialStoredDomainStatus = readStorageValue(DOMAIN_STATUS_KEY)
  const initialStoredEmail = readStorageValue(SESSION_EMAIL_KEY)

  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    form: { ...initialState.form, email: initialStoredEmail },
    confirm: { code: codeStr || '', password: '' },
    userSub: userStr || (isBrowser ? window.sessionStorage.getItem('userSub') : ''),
    domainStatus: initialStoredDomainStatus
  })

  const { ensure } = useCsrf()
  const { signup, signupReset, forgotPasswordInit, loading } = useAuth()
  const [csrf, setCsrf] = useState('')
  const [showAccessReminder, setShowAccessReminder] = useState(false)
  const [resendMessage, setResendMessage] = useState('')
  const [resendEmail, setResendEmail] = useState(state.form.email || initialStoredEmail || '')
  const [latestDomainStatus, setLatestDomainStatus] = useState(initialStoredDomainStatus)

  const domainState = useDomainChecker((state.form.email || '').trim())
  const resendDomainState = useDomainChecker((resendEmail || '').trim())
  const hasAcknowledgedAccess = Boolean(state.form.request_institutional_access)
  const signupInitialRef = useRef({ email: state.form.email || '', given_name: '', family_name: '', request_institutional_access: state.form.request_institutional_access || false })
  const confirmInitialRef = useRef({ code: codeStr || '', password: '' })

  const persistDomainStatus = useCallback((status) => {
    if (!status || status === 'checking') return
    writeStorageValue(DOMAIN_STATUS_KEY, status)
    setLatestDomainStatus(prev => (prev === status ? prev : status))
  }, [])

  useEffect(() => { persistDomainStatus(domainState) }, [domainState, persistDomainStatus])
  useEffect(() => { persistDomainStatus(resendDomainState) }, [resendDomainState, persistDomainStatus])

  useEffect(() => {
    if (userStr && codeStr) {
      dispatch({ type: 'USER_SUB', sub: userStr })
      if (isBrowser && userStr) window.sessionStorage.setItem('userSub', userStr)
      dispatch({ type: 'SET_CONFIRM_CODE', code: codeStr })
      dispatch({ type: 'SET_STAGE', stage: STAGE.CONFIRM })
    }
  }, [userStr, codeStr])

  useEffect(() => {
    ensure().then(setCsrf)
  }, [ensure])

  const domainBannerText = useMemo(() => {
    if (domainState === 'checking') return 'Checking email domain…'
    if (domainState === 'valid') return 'Business email detected.'
    if (domainState === 'invalid') return 'Institutional access requires a business email; you can verify one later.'
    return ''
  }, [domainState])

  const resendBannerText = useMemo(() => {
    if (!resendEmail) return ''
    if (resendDomainState === 'checking') return 'Checking email domain…'
    if (resendDomainState === 'valid') return 'Business email detected.'
    if (resendDomainState === 'invalid') return 'Institutional access requires a business email; you may verify later.'
    return ''
  }, [resendDomainState, resendEmail])

  const handleEmailInput = useCallback((e) => {
    const v = e.target.value
    dispatch({ type: 'FORM', data: { ...state.form, email: v } })
    const trimmed = v.trim()
    writeStorageValue(SESSION_EMAIL_KEY, trimmed)
  }, [state.form])

  const emailFields = useMemo(
    () => [
      { type: 'input', props: { id: 'email', name: 'email', label: 'Email', type: 'email', required: true, autoComplete: 'email', freevalidation: 'false', helperText: domainState === 'invalid' ? 'Institutional access requires a business email; you may verify later.' : '', onInput: handleEmailInput } },
      { type: 'input', props: { id: 'given_name', name: 'given_name', label: 'First name', group: 'name' } },
      { type: 'input', props: { id: 'family_name', name: 'family_name', label: 'Last name', group: 'name' } }
    ],
    [domainState, handleEmailInput]
  )

  const confirmFields = useMemo(
    () => [
      { type: 'input', props: { id: 'code', name: 'code', label: 'Verification code', required: true, autoComplete: 'one-time-code', inputMode: 'numeric' } },
      {
        type: 'note',
        props: {
          id: 'password-guidance',
          variant: 'body2',
          className: styles.passwordIntro,
          text: 'You are creating your Harbor password for the first time. Choose a password that meets the requirements below.'
        }
      },
      {
        type: 'input',
        props: {
          id: 'password',
          name: 'password',
          label: 'Create your password',
          type: 'password',
          required: true,
          autoComplete: 'new-password',
          initial: true,
          passwordPolicy: true,
          helperText: 'Follow the requirements listed below.',
          ariaDescribedBy: 'password-guidance'
        }
      }
    ],
    []
  )

  const handleForgotPasswordInit = useCallback(async () => {
    try {
      const email = (resendEmail || '').trim()
      if (!email) {
        setResendMessage('Enter your email address to request a new code.')
        return
      }
      writeStorageValue(SESSION_EMAIL_KEY, email)
      const r = await forgotPasswordInit(email)
      if (r.error) {
        setResendMessage(r.error)
        return
      }
      if (r.userSub && r.userSub !== state.userSub) {
        dispatch({ type: 'USER_SUB', sub: r.userSub })
        if (isBrowser) window.sessionStorage.setItem('userSub', r.userSub)
      }
      setResendMessage('A verification code has been sent to your email.')
    } catch (error) {
      console.error('Error requesting new code', error)
      setResendMessage(error?.message || 'Unable to request a new code right now.')
    }
  }, [forgotPasswordInit, resendEmail, state.userSub])

  const handleResendEmailChange = useCallback((event) => {
    const { value } = event.target
    setResendEmail(value)
    const trimmed = value.trim()
    writeStorageValue(SESSION_EMAIL_KEY, trimmed)
  }, [setResendEmail])

  const handleGateAcknowledgementChange = useCallback((event) => {
    const { checked } = event.target
    const nextForm = { ...state.form, request_institutional_access: checked }
    dispatch({ type: 'FORM', data: nextForm })
    signupInitialRef.current = { ...signupInitialRef.current, request_institutional_access: checked }
    if (checked) setShowAccessReminder(false)
  }, [dispatch, setShowAccessReminder, state.form])

  const guardAccess = useCallback((action) => {
    if (loading) return
    if (!hasAcknowledgedAccess) {
      setShowAccessReminder(true)
      return
    }
    setResendMessage('')
    return action?.()
  }, [hasAcknowledgedAccess, setShowAccessReminder, loading])

  const handleSignup = useCallback(async v => {
    setResendMessage('')
    if (!hasAcknowledgedAccess) {
      setShowAccessReminder(true)
      throw new Error('Please confirm your institutional access request before continuing.')
    }
    const resolvedDomainStatus = (() => {
      const statusChain = [domainState, latestDomainStatus]
      for (const status of statusChain) {
        if (status && status !== 'checking') return status
      }
      return ''
    })()
    if (resolvedDomainStatus) {
      persistDomainStatus(resolvedDomainStatus)
      dispatch({ type: 'DOMAIN', status: resolvedDomainStatus })
    }
    const trimmedEmail = (v.email || '').trim()
    writeStorageValue(SESSION_EMAIL_KEY, trimmedEmail)
    const r = await signup(v.email, v.given_name, v.family_name, hasAcknowledgedAccess, resolvedDomainStatus || undefined)
    if (r.error) throw new Error(r.error)
    dispatch({ type: 'USER_SUB', sub: r.userSub })
    if (isBrowser && r.userSub) window.sessionStorage.setItem('userSub', r.userSub)
    dispatch({ type: 'SET_STAGE', stage: STAGE.THANKYOU })
    return {}
  }, [signup, domainState, latestDomainStatus, hasAcknowledgedAccess, persistDomainStatus, setShowAccessReminder, dispatch])

  const handleConfirm = useCallback(async v => {
    setResendMessage('')
    const domainStatus = (() => {
      const candidates = [
        resendDomainState,
        latestDomainStatus,
        state.domainStatus,
        readStorageValue(DOMAIN_STATUS_KEY)
      ]
      for (const status of candidates) {
        if (status && status !== 'checking') return status
      }
      return ''
    })()
    const resolvedEmail = (() => {
      const storedEmail = readStorageValue(SESSION_EMAIL_KEY)
      const candidates = [
        (resendEmail || '').trim(),
        (state.form.email || '').trim(),
        (storedEmail || '').trim()
      ]
      return candidates.find(Boolean) || ''
    })()
    const code = v.code || state.confirm.code || codeStr || ''
    if (resolvedEmail) writeStorageValue(SESSION_EMAIL_KEY, resolvedEmail)
    const r = await signupReset(code, state.userSub, v.password, domainStatus || undefined, resolvedEmail || undefined)
    if (r.error) throw new Error(r.error)
    dispatch({ type: 'DONE' })
    dispatch({ type: 'SET_STAGE', stage: STAGE.FINAL_THANKS })
    writeStorageValue(SESSION_EMAIL_KEY, '')
    writeStorageValue(DOMAIN_STATUS_KEY, '')
    if (isBrowser) window.sessionStorage.removeItem('userSub')
    onSuccess?.()
    return {}
  }, [signupReset, state.userSub, state.confirm.code, codeStr, onSuccess, dispatch, resendEmail, state.form.email, resendDomainState, latestDomainStatus, state.domainStatus])

  const handleLinkedInClick = useCallback(() => guardAccess(() => startOauthFlow('LinkedIn')), [guardAccess])
  const handleGoogleClick = useCallback(() => guardAccess(() => startOauthFlow('Google')), [guardAccess])
  const handleAppleClick = useCallback(() => guardAccess(() => startOauthFlow('SignInWithApple')), [guardAccess])
  const handleLoginClick = useCallback(() => { if (isBrowser) window.location.href = '/institutional/login/' }, [])

  const onSignupValuesChange = useCallback((d) => {
    dispatch({
      type: 'FORM',
      data: {
        ...d,
        request_institutional_access: state.form.request_institutional_access
      }
    })
  }, [dispatch, state.form.request_institutional_access])
  const onConfirmValuesChange = useCallback((d) => dispatch({ type: 'CONFIRM', data: d }), [])

  useEffect(() => {
    if (codeStr) confirmInitialRef.current = { ...confirmInitialRef.current, code: codeStr }
  }, [codeStr])

  const cfg = useMemo(() => ({
    formId: state.stage === STAGE.CONFIRM ? 'cognito-confirm' : 'cognito-signup',
    fields: state.stage === STAGE.CONFIRM ? confirmFields : emailFields,
    button: state.stage === STAGE.CONFIRM ? 'Verify & set password' : 'Sign up',
    onSubmit: state.stage === STAGE.CONFIRM ? handleConfirm : handleSignup,
    initialValues: state.stage === STAGE.CONFIRM ? confirmInitialRef.current : signupInitialRef.current,
    onChange: state.stage === STAGE.CONFIRM ? onConfirmValuesChange : onSignupValuesChange
  }), [state.stage, confirmFields, emailFields, handleConfirm, handleSignup, onConfirmValuesChange, onSignupValuesChange])

  const primaryButtonStyles = useMemo(() => {
    const gated = state.stage === STAGE.SIGNUP && !hasAcknowledgedAccess
    return {
      width: '100%',
      padding: '12px 16px',
      fontSize: '16px',
      fontWeight: '600',
      borderRadius: '16px',
      marginTop: '16px',
      marginBottom: '0px',
      cursor: gated ? 'not-allowed' : 'pointer',
      opacity: gated ? 0.6 : 1
    }
  }, [hasAcknowledgedAccess, state.stage])

  useEffect(() => {
    if (state.stage === STAGE.CONFIRM) {
      setResendEmail((prev) => {
        if (prev) return prev
        if (state.form.email) return state.form.email
        const stored = readStorageValue(SESSION_EMAIL_KEY) || ''
        return stored
      })
    }
  }, [state.stage, state.form.email])

  useEffect(() => {
    const trimmed = (state.form.email || '').trim()
    writeStorageValue(SESSION_EMAIL_KEY, trimmed)
  }, [state.form.email])

  return (
    <section className={styles.root} aria-busy={loading}>
      <div className={styles.header}>
        <HarborLogo title="Harbor Capital Logo" width={150} height={58} className={styles.logo} />
      </div>
      <div className={styles.formRow}>
        <div className={styles.panel}>
          {state.stage === STAGE.SIGNUP && (
            <>
              <div className={styles.registerDisclosure}>
                <p className={styles.disclosureTitle}><strong>Please review the following information if you are requesting Institutional access to the Harbor Capital&apos;s website:</strong></p>
                <p>THE INFORMATION ON THIS WEBSITE IS FOR INSTITUTIONAL INVESTOR USE ONLY AND IS NOT INTENDED FOR RETAIL DISTRIBUTION.</p>
                <p>By accessing this website, you certify that you are an Institutional Investor or are an agent acting on behalf of an Institutional Investor and are exercising independent judgment in your evaluation of any material on this website. &quot;Institutional Investors&quot; are banks, savings and loan associations, insurance companies, registered investment companies, registered investment advisers, government entities, employee benefit plans and qualified plans with at least 100 participants, FINRA members and registered persons, and any entity (including a natural person) with assets of at least $50 million.</p>
                <p>The information contained on this website is intended for use by Institutional Investors in the U.S. only. It is not intended for use by non-U.S. entities or for retail investors.</p>
                <p>The information contained on this website is current as of the date of issuance (or such earlier date as referenced herein) and is subject to change without notice. The information contained herein reflects information obtained from sources believed to be reliable but are not guaranteed as such.</p>
                <p>By accessing this website you agree that (i) your use of the website and the content contained herein is subject to the terms on this notice, the <a target="_blank" rel="noreferrer" href="https://assets.harborcapital.com/docs/HF_Terms_and_Conditions.pdf"><u>Terms of Use</u></a> in effect for the website, and the <a target="_blank" rel="noreferrer" href="https://assets.harborcapital.com/docs/Privacy_Policy.pdf"><u>Privacy Policy</u></a>; and (ii) you will not distribute material designated for Institutional Investor Use Only found on the website to persons who are not Institutional Investors or agents acting on behalf of Institutional Investors.</p>
              </div>
              <div className={styles.accessGate}>
                <label htmlFor="request_institutional_access" className={styles.accessCheckbox}>
                  <input
                    id="request_institutional_access"
                    name="request_institutional_access"
                    type="checkbox"
                    checked={hasAcknowledgedAccess}
                    onChange={handleGateAcknowledgementChange}
                  />
                  <span>Request Institutional Access</span>
                </label>
                <div className={styles.checkboxNote}>
                  <p className={styles.muted}>By clicking &quot;Request Institutional Access&quot;, you acknowledge that you are an Institutional Investor as defined above, the information you provided is accurate, and you accept and agree to the website terms of use.</p>
                </div>
                {(!hasAcknowledgedAccess || showAccessReminder) && (
                  <div
                    className={styles.accessReminder}
                    role={showAccessReminder ? 'alert' : 'status'}
                    aria-live="polite"
                  >
                    Select the checkbox above to unlock every sign up option.
                  </div>
                )}
              </div>
              <div className={styles.social}>
                <button
                  className={styles.btnOutlinedFull}
                  onClick={handleGoogleClick}
                  data-gated={!hasAcknowledgedAccess}
                  aria-disabled={!hasAcknowledgedAccess}
                  type="button"
                  disabled={loading || !hasAcknowledgedAccess}
                >
                  <GoogleLogo title="Google Logo" height={24} width={24} />
                  <span className={styles.btnLabelStrong}>Sign up with Google</span>
                </button>
                <button
                  className={styles.btnOutlinedFull}
                  onClick={handleLinkedInClick}
                  data-gated={!hasAcknowledgedAccess}
                  aria-disabled={!hasAcknowledgedAccess}
                  type="button"
                  disabled={loading || !hasAcknowledgedAccess}
                >
                  <LinkedInLogo title="LinkedIn Logo" height={24} width={24} />
                  <span className={styles.btnLabelStrong}>Sign up with LinkedIn</span>
                </button>
                <button
                  className={styles.btnOutlinedFull}
                  onClick={handleAppleClick}
                  data-gated={!hasAcknowledgedAccess}
                  aria-disabled={!hasAcknowledgedAccess}
                  type="button"
                  disabled={loading || !hasAcknowledgedAccess}
                >
                  <AppleLogo title="Apple Logo" height={24} width={24} />
                  <span className={styles.btnLabelStrong}>Sign up with Apple</span>
                </button>
              </div>
            </>
          )}
          {state.stage === STAGE.THANKYOU && (
            <div className={styles.center}>
              <h3 className={styles.title}>Thank you for registering!</h3>
              <p className={styles.lead}>Please check your inbox for a confirmation email and click the link to complete your registration.</p>
            </div>
          )}
          {state.stage === STAGE.FINAL_THANKS && (
            <div className={styles.center}>
              <h3 className={styles.title}>Account confirmed!</h3>
              <p className={styles.lead}>Your account has been verified. You can now log in.</p>
            </div>
          )}
        </div>
        <div className={styles.panel}>
          {state.stage === STAGE.SIGNUP && <OrDivider />}
          {domainBannerText && state.stage === STAGE.SIGNUP && (
            <div className={
              domainState === 'checking' ? styles.statusChecking :
              domainState === 'valid' ? styles.statusValid :
              domainState === 'invalid' ? styles.statusInvalid : styles.statusInfo
            }>
              {domainState === 'checking' && <span className={styles.spinner} aria-hidden="true" />}
              <span>{domainBannerText}</span>
            </div>
          )}
          {(state.stage === STAGE.SIGNUP || state.stage === STAGE.CONFIRM) && (
            <div className={styles.emailForm}>
              <CustomForm
                key={cfg.formId}
                formId={cfg.formId}
                formFields={cfg.fields}
                initialValues={cfg.initialValues}
                ctaText={cfg.button}
                onSubmit={cfg.onSubmit}
                formCSRF={csrf}
                hideRecaptcha={true}
                onValuesChange={cfg.onChange}
                buttonStyles={primaryButtonStyles}
                submitDisabled={loading || (state.stage === STAGE.SIGNUP && !hasAcknowledgedAccess)}
              />
            </div>
          )}
          {state.done && (
            <div className={styles.afterLink}>
              <a href="/institutional/login/">Go to Login</a>
            </div>
          )}
        </div>
        {state.stage === STAGE.SIGNUP && (
          <div className={styles.panel}>
            <div className={styles.centerRow}>
              <p className={styles.muted}>Already registered?</p>
              <button className={styles.btnText} onClick={handleLoginClick}>Sign in</button>
            </div>
          </div>
        )}
        {state.stage === STAGE.CONFIRM && (
          <div className={styles.panel}>
            <div className={styles.center}>
              <div className={styles.requestCodeBox}>
                <label htmlFor="resend-email" className={styles.requestLabel}>Need a new verification code?</label>
                <div className={styles.requestCodeRow}>
                  <input
                    id="resend-email"
                    name="resend_email"
                    type="email"
                    value={resendEmail}
                    onChange={handleResendEmailChange}
                    placeholder="Enter your email"
                    className={styles.requestCodeInput}
                    autoComplete="email"
                  />
                  <button
                    className={styles.requestCodeButton}
                    type="button"
                    onClick={handleForgotPasswordInit}
                    disabled={loading}
                    aria-disabled={loading}
                  >
                    Request Code
                  </button>
                </div>
                {resendBannerText && (
                  <div
                    className={
                      resendDomainState === 'checking' ? styles.statusChecking :
                      resendDomainState === 'valid' ? styles.statusValid :
                      resendDomainState === 'invalid' ? styles.statusInvalid :
                      styles.statusInfo
                    }
                    aria-live="polite"
                    role="status"
                  >
                    {resendDomainState === 'checking' && <span className={styles.spinner} aria-hidden="true" />}
                    <span>{resendBannerText}</span>
                  </div>
                )}
                {resendMessage && (
                  <div className={styles.statusInfo} role="status" aria-live="polite">
                    <span>{resendMessage}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}
